import React, { useMemo, useState, useEffect } from 'react'
import {
    useTheme,
    Chip,
    TextField,
    IconButton,
    CircularProgress,
    Button,
    Tooltip,
} from '@material-ui/core'
import SearchIcon from '@material-ui/icons/Search'
import TuneIcon from '@material-ui/icons/Tune'
import EditIcon from '@material-ui/icons/Edit'
import BarChartIcon from '@material-ui/icons/BarChart'
import {
    Lease,
    Issue,
    Unit,
    AreaConfig,
    InventoryConfig,
    ModelMap,
    Service,
    ListVendor,
    User,
    Schedule,
} from '../../../../models'
import { toMMDDYYYY } from '../../../../helpers'
import { Container } from '../../../../components'
import { IssueRow } from './IssueRow'
import { PaginationController } from '../../../DamageHome/MoveInspectionContainer'
import { LeaseHomeChips } from './LeaseHomeChips'
import { IssueWorkorderModal } from '../../../DamageHome/IssueWorkorderModal'
import { IssueAnalyticsModal } from './IssueAnalyticsModal'
import {
    AutoCompleteFilterValue,
    DateFilterState,
    IssueFilterValue,
    IssueHasImageFilterValue,
    IssueWorkorderFilterValue,
    LeaseStatusFilterState,
    PetFilterValue,
    TenantInspectionStatusFilterState,
} from '../../../../hooks/useLeaseFilter'
import { FinderSelection } from '../../../../hooks'
import { useWorkorderApi } from '../../../../contexts'
import { toast } from 'react-toastify'
import { typographyStyle } from '../../../../theme/typographyStyle'

interface IssueData {
    issue: Issue
    lease: Lease
    areaName: string
    unitName: string
    tenantName: string
    tenantEmail: string
    startDate: string
    endDate: string
    inventoryConfigName: string
}

interface LeaseHomeIssuesTabProps {
    getFilteredLeases: (leaseList: Lease[], unitMap: ModelMap<Unit>) => Lease[]
    leaseList: Lease[]
    unitMap: ModelMap<Unit>
    areaConfigMap: ModelMap<AreaConfig>
    invConfigMap: ModelMap<InventoryConfig>
    filterDrawerOpen: boolean
    setFilterDrawerOpen: (open: boolean) => void
    searchValue: string
    setSearchValue: (val: string) => void
    dateFilter: DateFilterState
    setDateFilter: (dateFilter: DateFilterState) => void
    locationFinderSelection: FinderSelection
    statusFilter: LeaseStatusFilterState
    setStatusFilter: (sf: LeaseStatusFilterState) => void
    hasIssue: IssueFilterValue
    setHasIssue: (hasIssue: IssueFilterValue) => void
    issueWorkorderFilter: IssueWorkorderFilterValue
    setIssueWorkorderFilter: (filter: IssueWorkorderFilterValue) => void
    tenantInspectionStatusFilter: TenantInspectionStatusFilterState
    setTenantInspectionStatusFilter: (
        newFilter: TenantInspectionStatusFilterState,
    ) => void
    autoCompleteFilter: AutoCompleteFilterValue
    setAutoCompleteFilter: (val: AutoCompleteFilterValue) => void
    isLoading: boolean
    serviceList: Service[]
    vendorList: ListVendor[]
    userList: User[]
    scheduleList: Schedule[]
    onLeaseUpdate?: (updatedLease: Lease) => void
    issueHasImageFilter: IssueHasImageFilterValue
    setIssueHasImageFilter: (val: IssueHasImageFilterValue) => void
    petFilter: PetFilterValue
    setPetFilter: (val: PetFilterValue) => void
}

export const LeaseHomeIssuesTab = (props: LeaseHomeIssuesTabProps) => {
    const {
        getFilteredLeases,
        leaseList,
        unitMap,
        areaConfigMap,
        invConfigMap,
        filterDrawerOpen,
        setFilterDrawerOpen,
        searchValue,
        setSearchValue,
        dateFilter,
        setDateFilter,
        locationFinderSelection,
        statusFilter,
        setStatusFilter,
        hasIssue,
        setHasIssue,
        issueWorkorderFilter,
        setIssueWorkorderFilter,
        tenantInspectionStatusFilter,
        setTenantInspectionStatusFilter,
        autoCompleteFilter,
        setAutoCompleteFilter,
        isLoading,
        serviceList,
        vendorList,
        userList,
        scheduleList,
        onLeaseUpdate,
        issueHasImageFilter,
        setIssueHasImageFilter,
        petFilter,
        setPetFilter,
    } = props

    const theme = useTheme()
    const { createWorkorders } = useWorkorderApi()

    // Local state
    const [pageSize, setPageSize] = useState(25)
    const [page, setPage] = useState(0)
    const [selectionMode, setSelectionMode] = useState(false)
    const [selectedIssues, setSelectedIssues] = useState<IssueData[]>([])
    const [openWorkorderModal, setOpenWorkorderModal] = useState(false)
    const [workorderLoading, setWorkorderLoading] = useState(false)
    const [issuesSearchValue, setIssuesSearchValue] = useState('')
    const [openAnalyticsModal, setOpenAnalyticsModal] = useState(false)

    const filteredLeases = getFilteredLeases(leaseList, unitMap)

    // Extract all issues from filtered leases
    const allIssues = useMemo(() => {
        const issues: IssueData[] = []

        const extractIssuesFromLease = (lease: Lease, isChild = false) => {
            if (lease.tenant_move_in_inspection?.inventory_inspection) {
                lease.tenant_move_in_inspection.inventory_inspection.forEach(
                    (invIns) => {
                        if (invIns.issue) {
                            invIns.issue.forEach((issue) => {
                                const areaConfig =
                                    areaConfigMap[lease.area.area_config]
                                const unit = unitMap[lease.area.unit]
                                const invConfig =
                                    invConfigMap[invIns.inventory_config]

                                issues.push({
                                    issue,
                                    lease,
                                    areaName:
                                        areaConfig?.name || 'Unknown Area',
                                    unitName: unit?.name || 'Unknown Unit',
                                    tenantName: lease.user.name,
                                    tenantEmail: lease.user.email,
                                    startDate: toMMDDYYYY(
                                        new Date(lease.start_date),
                                    ),
                                    endDate: toMMDDYYYY(
                                        new Date(lease.end_date),
                                    ),
                                    inventoryConfigName:
                                        invConfig?.name || 'Unknown Item',
                                })
                            })
                        }
                    },
                )
            }
        }

        filteredLeases.forEach((parentLease) => {
            // Add parent lease issues
            extractIssuesFromLease(parentLease)

            // Add child lease issues
            if (parentLease.children) {
                parentLease.children.forEach((childLease) => {
                    extractIssuesFromLease(childLease, true)
                })
            }
        })

        return issues
    }, [filteredLeases, unitMap, areaConfigMap, invConfigMap])

    // Filter issues based on search value and workorder filter
    const filteredIssues = useMemo(() => {
        let filtered = allIssues

        // Apply workorder filter
        if (issueWorkorderFilter === IssueWorkorderFilterValue.HAS_WORKORDER) {
            filtered = filtered.filter(
                (issueData) => issueData.issue.workorder_id,
            )
        } else if (
            issueWorkorderFilter === IssueWorkorderFilterValue.NO_WORKORDER
        ) {
            filtered = filtered.filter(
                (issueData) => !issueData.issue.workorder_id,
            )
        }

        if (issueHasImageFilter === IssueHasImageFilterValue.HAS_IMAGE) {
            filtered = filtered.filter(
                (issueData) => issueData.issue.message.attachments.length > 0,
            )
        } else if (issueHasImageFilter === IssueHasImageFilterValue.NO_IMAGE) {
            filtered = filtered.filter(
                (issueData) => issueData.issue.message.attachments.length === 0,
            )
        }

        // Apply search filter
        if (!issuesSearchValue || !issuesSearchValue.trim()) {
            return filtered
        }

        const searchLower = issuesSearchValue.toLowerCase()
        return filtered.filter((issueData) => {
            return (
                (issueData.tenantName || '')
                    .toLowerCase()
                    .includes(searchLower) ||
                (issueData.tenantEmail || '')
                    .toLowerCase()
                    .includes(searchLower) ||
                (issueData.unitName || '')
                    .toLowerCase()
                    .includes(searchLower) ||
                (issueData.areaName || '')
                    .toLowerCase()
                    .includes(searchLower) ||
                (issueData.inventoryConfigName || '')
                    .toLowerCase()
                    .includes(searchLower) ||
                (issueData.issue.description || '')
                    .toLowerCase()
                    .includes(searchLower)
            )
        })
    }, [allIssues, issuesSearchValue, issueWorkorderFilter])

    // Pagination
    const numPages = Math.ceil((filteredIssues?.length ?? 0) / pageSize)
    const paginatedIssues = useMemo(() => {
        const start = page * pageSize
        const end = start + pageSize
        return filteredIssues?.slice(start, end)
    }, [page, filteredIssues, pageSize])

    // Reset to first page when filters change
    useEffect(() => {
        if (page + 1 > numPages) {
            setPage(Math.max(0, numPages - 1))
        }
    }, [filteredIssues, numPages])

    // Reset selection when lease list changes
    useEffect(() => {
        setSelectedIssues([])
        setSelectionMode(false)
    }, [leaseList])

    // Issue selection handlers
    const handleIssueSelection = (issueData: IssueData) => {
        if (issueData.issue.workorder_id) return

        if (
            selectedIssues.find(
                (selectedIssue) =>
                    selectedIssue.issue.id === issueData.issue.id,
            )
        ) {
            setSelectedIssues(
                selectedIssues.filter(
                    (selectedIssue) =>
                        selectedIssue.issue.id !== issueData.issue.id,
                ),
            )
        } else {
            setSelectedIssues([...selectedIssues, issueData])
        }
    }

    const isIssueSelected = (issueData: IssueData) => {
        return (
            selectedIssues.find(
                (selectedIssue) =>
                    selectedIssue.issue.id === issueData.issue.id,
            ) !== undefined
        )
    }

    // Update issue state when workorders are created
    const updateIssueWithWorkorder = (issueId: number, workorderId: number) => {
        const issueData = selectedIssues.find(
            (issueData) => issueData.issue.id === issueId,
        )
        if (!issueData || !onLeaseUpdate) return

        const lease = issueData.lease
        const updatedLease = { ...lease }

        // Helper function to update issues in a lease
        const updateIssuesInLease = (lease: Lease) => {
            lease.tenant_move_in_inspection?.inventory_inspection?.forEach(
                (inv) => {
                    if (inv.issue) {
                        inv.issue = inv.issue.map((innerIssue) => {
                            if (issueId !== innerIssue.id) {
                                return innerIssue
                            }
                            return {
                                ...innerIssue,
                                workorder_id: workorderId,
                            }
                        })
                    }
                },
            )
        }

        updateIssuesInLease(updatedLease)

        // If this is a child lease, update the parent lease's children array
        if (lease.parent) {
            const parentLease = leaseList.find((l) => l.id === lease.parent)
            if (parentLease) {
                const updatedParentLease = { ...parentLease }
                if (updatedParentLease.children) {
                    updatedParentLease.children = updatedParentLease.children.map(
                        (child) =>
                            child.id === lease.id ? updatedLease : child,
                    )
                }
                onLeaseUpdate(updatedParentLease)
                return
            }
        }

        onLeaseUpdate(updatedLease)
    }

    // Workorder creation handler
    const handleCreateWorkorders = (request: any) => {
        setWorkorderLoading(true)
        createWorkorders(request)
            .then((response) => {
                toast.success('Workorders created successfully!')

                // Update issue states with workorder IDs
                if (
                    response?.workorders &&
                    Array.isArray(response.workorders)
                ) {
                    response.workorders.forEach((workorder: any) => {
                        if (workorder.issue_id) {
                            updateIssueWithWorkorder(
                                workorder.issue_id,
                                workorder.id,
                            )
                        }
                    })
                }
                setOpenWorkorderModal(false)
                setSelectedIssues([])
                // Stay in selection mode, just clear the selection
            })
            .catch((error) => {
                toast.error(
                    error.response?.data?.message ||
                        'Error creating workorders',
                )
            })
            .finally(() => {
                setWorkorderLoading(false)
            })
    }

    return (
        <Container
            style={{ flexDirection: 'column', flex: 1, overflow: 'hidden' }}
        >
            {/* Header */}
            <Container
                style={{
                    marginBottom: theme.spacing(1),
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    flexShrink: 0,
                }}
            >
                <span
                    style={{
                        ...typographyStyle(theme.typography.h4),
                        fontWeight: 'bold',
                        color: theme.palette.grey[800],
                    }}
                >
                    Issues
                </span>

                <Container
                    style={{ alignItems: 'center', gap: theme.spacing(2) }}
                >
                    {/* Selection Mode UI */}
                    {selectionMode && (
                        <Container
                            style={{
                                alignItems: 'center',
                                marginRight: theme.spacing(2),
                            }}
                        >
                            <span
                                style={{
                                    ...typographyStyle(theme.typography.h6),
                                    fontWeight: 700,
                                    marginRight: theme.spacing(2),
                                }}
                            >
                                {selectedIssues.length} Selected
                            </span>
                            <Button
                                disabled={selectedIssues.length === 0}
                                variant="contained"
                                style={{
                                    backgroundColor: theme.palette.primary.dark,
                                    color: 'white',
                                    textTransform: 'none',
                                }}
                                onClick={() => setOpenWorkorderModal(true)}
                                size="small"
                            >
                                + Create Workorders
                            </Button>
                        </Container>
                    )}

                    <Tooltip title="Analytics">
                        <IconButton
                            onClick={() => setOpenAnalyticsModal(true)}
                            style={{ marginRight: theme.spacing(1) }}
                        >
                            <BarChartIcon />
                        </IconButton>
                    </Tooltip>

                    <Tooltip title="Select Issues">
                        <IconButton
                            onClick={() => setSelectionMode(!selectionMode)}
                            color={selectionMode ? 'primary' : 'default'}
                            style={{ marginRight: theme.spacing(1) }}
                        >
                            <EditIcon />
                        </IconButton>
                    </Tooltip>

                    <Chip
                        label={`${filteredIssues.length} Total Issues`}
                        style={{
                            backgroundColor: theme.palette.primary.main,
                            color: 'white',
                            fontWeight: 'bold',
                        }}
                    />
                </Container>
            </Container>

            {/* Filter Chips */}
            <LeaseHomeChips
                dateFilter={dateFilter}
                setDateFilter={setDateFilter}
                locationFinderSelection={locationFinderSelection}
                statusFilter={statusFilter}
                setStatusFilter={setStatusFilter}
                hasIssue={hasIssue}
                setHasIssue={setHasIssue}
                issueWorkorderFilter={issueWorkorderFilter}
                setIssueWorkorderFilter={setIssueWorkorderFilter}
                tenantInspectionStatusFilter={tenantInspectionStatusFilter}
                setTenantInspectionStatusFilter={
                    setTenantInspectionStatusFilter
                }
                autoCompleteFilter={autoCompleteFilter}
                setAutoCompleteFilter={setAutoCompleteFilter}
                petFilter={petFilter}
                setPetFilter={setPetFilter}
            />

            {/* Search and Filter Bar */}
            <Container
                style={{
                    alignItems: 'center',
                    marginBottom: theme.spacing(1),
                    gap: theme.spacing(2),
                    flexShrink: 0,
                }}
            >
                <TextField
                    value={issuesSearchValue}
                    onChange={(e) => setIssuesSearchValue(e.target.value)}
                    variant="outlined"
                    placeholder="Search issues..."
                    size="small"
                    InputProps={{
                        startAdornment: <SearchIcon htmlColor="#C2C2C2" />,
                    }}
                    style={{
                        borderRadius: '5px',
                        width: '300px',
                    }}
                />

                <div style={{ flex: 1 }} />

                <IconButton
                    onClick={() => setFilterDrawerOpen(!filterDrawerOpen)}
                    style={{
                        color: filterDrawerOpen
                            ? theme.palette.primary.main
                            : '#8A8A8A',
                    }}
                >
                    <TuneIcon />
                    <span
                        style={{
                            marginLeft: 8,
                            fontStretch: 'normal',
                            fontWeight: 400,
                            fontSize: '14px',
                            lineHeight: '16px',
                            color: filterDrawerOpen
                                ? theme.palette.primary.main
                                : '#8A8A8A',
                        }}
                    >
                        Filter
                    </span>
                </IconButton>
            </Container>

            {/* Issues List */}
            <Container
                style={{
                    flexDirection: 'column',
                    gap: theme.spacing(2),
                    flex: 1,
                    overflowY: 'auto',
                    minHeight: 0,
                }}
                className="scroll-bar"
            >
                {isLoading ? (
                    <Container
                        style={{
                            flex: 1,
                            alignItems: 'center',
                            justifyContent: 'center',
                        }}
                    >
                        <CircularProgress />
                    </Container>
                ) : (
                    paginatedIssues?.map((issueData, index) => (
                        <IssueRow
                            key={`ISSUE_${issueData.issue.id}_${index}`}
                            issueData={issueData}
                            unitMap={unitMap as Record<number, Unit>}
                            selectionMode={selectionMode}
                            isSelected={isIssueSelected(issueData)}
                            onSelectionChange={() =>
                                handleIssueSelection(issueData)
                            }
                            onClick={(data) => {
                                console.log('Issue clicked:', data)
                            }}
                        />
                    ))
                )}
            </Container>

            {/* Pagination */}
            <Container
                style={{
                    marginTop: theme.spacing(2),
                    justifyContent: 'center',
                    flexShrink: 0,
                }}
            >
                <PaginationController
                    page={page}
                    numPages={numPages}
                    listLength={filteredIssues.length ?? 0}
                    setPage={setPage}
                />
            </Container>

            {/* Issue Workorder Modal */}
            <IssueWorkorderModal
                open={openWorkorderModal}
                serviceList={serviceList}
                vendorList={vendorList}
                userList={userList}
                issues={selectedIssues.map((issueData) => issueData.issue)}
                scheduleList={scheduleList}
                areas={filteredLeases.map((lease) => lease.area)}
                issueAreaMap={Object.fromEntries(
                    selectedIssues.map((issueData) => [
                        issueData.issue.id,
                        issueData.lease.area,
                    ]),
                )}
                loading={workorderLoading}
                handleClose={() => setOpenWorkorderModal(false)}
                onClickCreate={handleCreateWorkorders}
            />

            {/* Analytics Modal */}
            <IssueAnalyticsModal
                open={openAnalyticsModal}
                statusFilter={statusFilter}
                onClose={() => setOpenAnalyticsModal(false)}
            />
        </Container>
    )
}
